import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  useTheme,
} from '@mui/material';
import { IconChartLine } from '@tabler/icons-react';
import { map } from 'lodash';
import { useCallback, memo } from 'react';
import MoedaText from '../../util/ui/inputs/moedaText';
import FelixCard from '../../util/ui/felixCard';
import ChartContainer from '../../util/charts/chartContainer';
import PosicaoAtivo from '../../../models/analises/posicaoAtivo';
import Linha from '../../util/charts/linha';
import { SliceTooltipProps } from '@nivo/line';
import moment from 'moment';
import { felixTokens, radius } from '../../../theme';

type PosicaoAtivoDataPoint = {
  x: string;
  y: number;
  aux: PosicaoAtivo;
};

type Props = {
  titulo: string;
  posicoes: PosicaoAtivo[];
  loading: boolean;
};

const TooltipPosicaoAtivo = memo(({ slice }: SliceTooltipProps) => {
  const pointData = slice.points[0].data as unknown as PosicaoAtivoDataPoint;
  return (
    // Tooltip de gráfico: surface + hairline vindos do Card do tema.
    <Card
      elevation={0}
      sx={{ boxShadow: felixTokens.shadow.overlay, borderRadius: radius.sm }}
    >
      <Box display="flex" flexDirection="column" padding={1.5} minWidth={160}>
        <Typography variant="labelMono" color="text.secondary" gutterBottom>
          {moment(pointData.x).format('DD [de] MMMM [de] YYYY')}
        </Typography>
        <Box
          display="flex"
          flexDirection="row"
          width="100%"
          justifyContent="space-between"
          gap={2}
        >
          <Typography variant="labelMono" color="text.secondary">
            PM
          </Typography>
          <Typography variant="dataMono" color="text.primary">
            <MoedaText value={pointData.aux.precoMedio} />
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          width="100%"
          justifyContent="space-between"
          gap={2}
        >
          <Typography variant="labelMono" color="text.secondary">
            Qtd
          </Typography>
          <Typography variant="dataMono" color="text.primary">
            {pointData.aux.quantidade}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
});

TooltipPosicaoAtivo.displayName = 'TooltipPosicaoAtivo';

export default function CardPosicaoAtivo({ titulo, posicoes, loading }: Props) {
  const theme = useTheme();

  const formatTooltip = useCallback(
    (props: SliceTooltipProps) => <TooltipPosicaoAtivo {...props} />,
    [],
  );

  const dataPM = [
    {
      id: 'Preço Médio',
      // Tinta do accent: `primary.dark` some sobre a superfície escura.
      color: theme.palette.accentInk,
      data: map(posicoes, (p) => ({
        x: p.data.format('YYYY-MM-DD'),
        y: p.precoMedio,
        aux: p,
      })),
    },
  ];
  const dataQtd = [
    {
      id: 'Quantidade',
      color: theme.palette.tertiary.main,
      data: map(posicoes, (p) => ({
        x: p.data.format('YYYY-MM-DD'),
        y: p.quantidade,
        aux: p,
      })),
    },
  ];

  return (
    <FelixCard>
      <CardHeader title={titulo} avatar={<IconChartLine size={16} />} />
      <CardContent>
        <ChartContainer
          height={300}
          emptyText="Ainda não há resultados mensais."
          isEmpty={dataPM.length === 0}
          isLoading={loading}
        >
          <Linha
            data={dataPM}
            tooltip={formatTooltip}
            curve="stepAfter"
            yFormat={(value) =>
              Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(value as number)
            }
            xMin={posicoes[0]?.data.format('YYYY-MM-DD')}
            xMax={posicoes[posicoes.length - 1]?.data.format('YYYY-MM-DD')}
          />
        </ChartContainer>
        <ChartContainer
          height={300}
          emptyText="Ainda não há resultados mensais."
          isEmpty={dataQtd.length === 0}
          isLoading={loading}
        >
          <Linha
            data={dataQtd}
            tooltip={formatTooltip}
            curve="stepAfter"
            yFormat={(value) => value.toString()}
            xMin={posicoes[0]?.data.format('YYYY-MM-DD')}
            xMax={posicoes[posicoes.length - 1]?.data.format('YYYY-MM-DD')}
          />
        </ChartContainer>
      </CardContent>
    </FelixCard>
  );
}
